import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardBody,
    Spinner, Modal, ModalHeader, ModalBody
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../Components/Common/BreadCrumb';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

const UserMetadata = () => {
    document.title = "User Metadata";
    
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGroupAttributes, setSelectedGroupAttributes] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/userMetaDataGroup`, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });

            const data = await response.json();
            if (data.data) {
                setGroups(data.data);
            }
        } catch (error) {
            console.error('Error fetching metadata groups:', error);
            toast.error("Failed to fetch metadata groups");
        } finally {
            setLoading(false);
        }
    };

    const fetchGroupAttributes = async (groupId) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/userMetaDataAttribute/byGroupId?groupId=${groupId}`, {
                headers: {
                    'Authorization': JSON.parse(sessionStorage.getItem("authUser"))?.token || '',
                }
            });

            const data = await response.json();
            if (data.data) {
                setSelectedGroupAttributes(data.data);
            }
        } catch (error) {
            console.error('Error fetching group attributes:', error);
            toast.error(error.message || 'Failed to fetch group attributes', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
        fetchGroupAttributes(group._id);
        setModalOpen(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
                
                <Container fluid>
                    <BreadCrumb title="User Metadata" pageTitle="Settings" />
                    <Row>
                        {groups.map((group) => (
                            <Col key={group._id} xl={3} md={6}>
                                <Card 
                                    className="card-animate" 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleGroupClick(group)}
                                >
                                    <CardBody>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <h5 className="fs-15 mb-3">
                                                    <i className="ri-database-2-line text-primary me-2"></i>
                                                    {group.groupTitle}
                                                </h5>
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0">
                                                        <div className="avatar-sm rounded bg-primary-subtle">
                                                            <span className="avatar-title rounded bg-primary-subtle text-primary">
                                                                <i className={`ri-${
                                                                    group.groupTitle === 'Initial' ? 'flag' :
                                                                    group.groupTitle === 'Basics' ? 'user' :
                                                                    group.groupTitle === 'Background & Identity' ? 'profile' :
                                                                    group.groupTitle === 'Family' ? 'group' :
                                                                    group.groupTitle === 'Koottu Kanvas' ? 'palette' :
                                                                    'shapes'
                                                                }-line fs-3`}></i>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1 ms-3">
                                                        <p className="text-muted mb-0">Click to view attributes</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>

            <Modal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                size="xl"
                scrollable
            >
                <ModalHeader toggle={() => setModalOpen(false)}>
                    {selectedGroup?.groupTitle} Attributes
                </ModalHeader>
                <ModalBody>
                    <Row>
                        {selectedGroupAttributes.map((attribute) => (
                            <Col key={attribute._id} lg={6} className="mb-4">
                                <Card className="border">
                                    <CardBody>
                                        <div className="d-flex align-items-center mb-3">
                                            <i className={`ri-${attribute.attributeKeyIconUrl.replace('.svg', '')}-line fs-22 text-primary me-2`}></i>
                                            <h5 className="card-title mb-0">{attribute.textShownOnSignUp}</h5>
                                        </div>
                                        <p className="text-muted small mb-3">
                                            Shown as "{attribute.textShownOnProfile}" on profile
                                        </p>
                                        <div className="mb-3">
                                            <span className="badge bg-primary-subtle text-primary me-2">
                                                {attribute.entryValueType}
                                            </span>
                                            {attribute.maxSelection && (
                                                <span className="badge bg-info-subtle text-info">
                                                    Max Selection: {attribute.maxSelection}
                                                </span>
                                            )}
                                        </div>
                                        <div className="values-container">
                                            {attribute.values.map((value, index) => (
                                                <span 
                                                    key={index} 
                                                    className="badge bg-light text-dark me-2 mb-2"
                                                    style={{ fontSize: '0.9em' }}
                                                >
                                                    {value.title}
                                                </span>
                                            ))}
                                        </div>
                                        {attribute.isFilterable && (
                                            <div className="mt-3 pt-2 border-top">
                                                <p className="text-muted small mb-2">
                                                    Filter Question: {attribute.textShownOnFiltersList}
                                                </p>
                                                <span className="badge bg-success-subtle text-success">
                                                    {attribute.filterValueType}
                                                </span>
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </ModalBody>
            </Modal>

            {loading && (
                <div className="overlay">
                    <div className="spinner-container">
                        <Spinner color="primary" />
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default UserMetadata;